import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import NumericInput from '@/components/NumericInput';
import OffDevicePosRefundDialog from '@/components/POS/OffDevicePosRefundDialog';
import {
  OffDeviceRefundResult,
  PosReconciliationReport,
  PosTransaction,
  PosTransactionStatus,
  posService
} from '@/data/posService';
import { businessDayToday } from '@/utils/businessDay';
import {
  isOffDeviceRefundTransaction,
  needsManualReview,
  readRefundOutcome
} from '@/utils/posRefund';

const STATUS_COLOR: Record<
  PosTransactionStatus,
  'success' | 'error' | 'warning' | 'info' | 'default'
> = {
  APPROVED: 'success',
  APPROVED_NOT_FINALIZED: 'warning',
  PENDING: 'info',
  WAITING_FOR_TERMINAL: 'info',
  LOCKED_BY_TERMINAL: 'info',
  UNKNOWN: 'warning',
  FAILED: 'error',
  CANCELLED: 'default',
  TIMEOUT: 'warning',
  REFUNDED: 'default',
  PARTIALLY_REFUNDED: 'warning',
  MANUAL_OVERRIDE: 'warning'
};

const IN_FLIGHT: PosTransactionStatus[] = [
  'PENDING',
  'WAITING_FOR_TERMINAL',
  'UNKNOWN'
];

interface Props {
  branchId: number;
}

// İşletme günü. Önceden `new Date().toISOString()` idi: bu UTC tarihidir,
// yani TR 00:00 ile 03:00 arasında YANLIŞ günü seçiyordu. Backend mutabakatı
// (`pos.service.reconcile`) businessDate'i işletme günü olarak yorumluyor.
const todayIso = () => businessDayToday();

/**
 * Şube Detayları, POS sekmesi: the branch's POS transactions, refunds and day
 * end reconciliation. Token / InPOS sales get "Cihaz dışında iade edildi"
 * instead of İade Et (neither vendor can refund through its API); domain
 * refund errors on those devices point staff here.
 */
const BranchPosTransactionsManager = ({ branchId }: Props) => {
  const { t } = useTranslation();
  const [transactions, setTransactions] = useState<PosTransaction[]>([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [businessDate, setBusinessDate] = useState(todayIso());
  const [refundTarget, setRefundTarget] = useState<PosTransaction | null>(null);
  const [refundAmount, setRefundAmount] = useState(0);
  const [refundReason, setRefundReason] = useState('');
  const [refundInFlight, setRefundInFlight] = useState(false);
  const [offDeviceTarget, setOffDeviceTarget] = useState<PosTransaction | null>(
    null
  );
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] =
    useState<PosReconciliationReport | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await posService.listTransactions({
        branchId,
        page: 1,
        limit: 50
      });
      setTransactions(result.items);
    } catch (err: any) {
      setError(err?.response?.data?.message ?? t('posTransactions.error.list'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchId]);

  const handleCancel = async (transaction: PosTransaction) => {
    setError(null);
    setMessage(null);
    try {
      await posService.cancelTransaction(transaction.id);
      setMessage(t('posTransactions.cancel.success', { id: transaction.id }));
      await load();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? t('posTransactions.cancel.error')
      );
    }
  };

  const openRefund = (transaction: PosTransaction) => {
    setError(null);
    setMessage(null);
    if (isOffDeviceRefundTransaction(transaction)) {
      setOffDeviceTarget(transaction);
      return;
    }
    setRefundTarget(transaction);
    setRefundAmount(Number(transaction.amount));
    setRefundReason('');
  };

  const submitRefund = async () => {
    if (!refundTarget) return;
    setError(null);
    setMessage(null);
    if (!refundAmount || refundAmount <= 0) {
      setError(t('posTransactions.refund.invalidAmount'));
      return;
    }
    const id = refundTarget.id;
    setRefundInFlight(true);
    try {
      const refund = await posService.refundTransaction(id, {
        amount: refundAmount,
        reason: refundReason || undefined
      });
      // A 201 is not a success: the device may have rejected the refund
      // (FAILED), or the money went back but the sale could not be undone.
      const outcome = readRefundOutcome(refund);
      if (outcome.kind === 'failed') {
        setError(outcome.message ?? t('posTransactions.refund.failed', { id }));
        return;
      }
      const after =
        outcome.kind === 'success'
          ? await posService.getTransaction(id).catch(() => undefined)
          : undefined;
      setRefundTarget(null);
      // Reload first: load() clears the error banner.
      await load();
      if (outcome.kind === 'pending') {
        setMessage(t('posTransactions.refund.pending', { id }));
      } else if (needsManualReview(after)) {
        setError(t('posTransactions.refund.manualReview', { id }));
      } else {
        setMessage(t('posTransactions.refund.success', { id }));
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.message ?? t('posTransactions.refund.error')
      );
    } finally {
      setRefundInFlight(false);
    }
  };

  const onOffDeviceRecorded = async (result: OffDeviceRefundResult) => {
    const id = result.transaction.id;
    setOffDeviceTarget(null);
    // Reload first: load() clears the error banner.
    await load();
    if (result.transaction.recoveryStatus === 'COMPLETED') {
      setMessage(t('posTransactions.offDevice.success', { id }));
    } else {
      setError(t('posTransactions.offDevice.notCompleted', { id }));
    }
  };

  const handleReconcile = async () => {
    setError(null);
    setMessage(null);
    setReconcileResult(null);
    setReconciling(true);
    try {
      const result = await posService.reconcile({ branchId, businessDate });
      setReconcileResult(result);
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          t('posTransactions.panel.reconcileError')
      );
    } finally {
      setReconciling(false);
    }
  };

  const statusLabel = (status: PosTransactionStatus) =>
    t(`posTransactions.status.${status}`);

  return (
    <Card sx={{ mt: 2 }}>
      <CardContent>
        <Stack spacing={2}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
          >
            <Box>
              <Typography variant="h5">{t('posTransactions.title')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('posTransactions.panel.description')}
              </Typography>
            </Box>
            <Button onClick={load} disabled={loading}>
              {t('posTransactions.panel.refresh')}
            </Button>
          </Box>

          {message && <Alert severity="success">{message}</Alert>}
          {error && <Alert severity="error">{error}</Alert>}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ sm: 'flex-end' }}
          >
            <TextField
              type="date"
              label={t('posTransactions.panel.businessDate')}
              value={businessDate}
              onChange={(event) => setBusinessDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Button
              variant="outlined"
              onClick={handleReconcile}
              disabled={reconciling}
            >
              {t('posTransactions.panel.reconcile')}
            </Button>
          </Stack>

          {reconcileResult && (
            <Alert
              severity={
                reconcileResult.unresolvedCount > 0 ? 'warning' : 'success'
              }
            >
              <Typography variant="body2" fontWeight="bold">
                {t('posTransactions.panel.reconcileSummary', {
                  date: reconcileResult.businessDate,
                  total: reconcileResult.totalCount,
                  approved: reconcileResult.approvedCount,
                  approvedTotal: reconcileResult.approvedTotal,
                  unresolved: reconcileResult.unresolvedCount
                })}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {t('posTransactions.panel.reconcileNote')}
              </Typography>
              {reconcileResult.unresolved.length > 0 && (
                <Stack spacing={0.5} mt={1}>
                  {reconcileResult.unresolved.map((transaction) => (
                    <Box
                      key={transaction.id}
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      gap={2}
                    >
                      <Typography variant="body2">
                        #{transaction.id} ·{' '}
                        {Number(transaction.amount).toFixed(2)}{' '}
                        {transaction.currency} ·{' '}
                        {t(
                          `posTransactions.targetType.${transaction.targetType}`
                        )}
                      </Typography>
                      <Chip
                        label={statusLabel(transaction.status)}
                        color={STATUS_COLOR[transaction.status] ?? 'default'}
                        size="small"
                      />
                    </Box>
                  ))}
                </Stack>
              )}
            </Alert>
          )}

          <Divider />

          <Stack spacing={1}>
            {transactions.map((transaction) => (
              <Box
                key={transaction.id}
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
              >
                <Box>
                  <Typography fontWeight="bold">
                    #{transaction.id} · {Number(transaction.amount).toFixed(2)}{' '}
                    {transaction.currency}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {t(`posTransactions.targetType.${transaction.targetType}`)}{' '}
                    · {transaction.provider?.name ?? '-'}
                    {transaction.providerErrorMessage
                      ? ` · ${transaction.providerErrorMessage}`
                      : ''}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    label={statusLabel(transaction.status)}
                    color={STATUS_COLOR[transaction.status] ?? 'default'}
                    size="small"
                  />
                  {(transaction.recoveryStatus === 'PENDING' ||
                    transaction.recoveryStatus === 'MANUAL_REVIEW') && (
                    <Typography
                      variant="caption"
                      color="warning.main"
                      display="block"
                    >
                      {transaction.recoveryStatus === 'PENDING'
                        ? t('posTransactions.panel.recoveryPending')
                        : t('posTransactions.panel.recoveryManual')}
                    </Typography>
                  )}
                  {transaction.status === 'APPROVED' && (
                    <Button
                      size="small"
                      onClick={() => openRefund(transaction)}
                    >
                      {isOffDeviceRefundTransaction(transaction)
                        ? t('posTransactions.action.offDevice')
                        : t('posTransactions.action.refund')}
                    </Button>
                  )}
                  {IN_FLIGHT.includes(transaction.status) && (
                    <Button
                      size="small"
                      color="error"
                      onClick={() => handleCancel(transaction)}
                    >
                      {t('posTransactions.action.cancel')}
                    </Button>
                  )}
                </Stack>
              </Box>
            ))}
            {transactions.length === 0 && !loading && (
              <Alert severity="info">{t('posTransactions.panel.empty')}</Alert>
            )}
          </Stack>
        </Stack>
      </CardContent>

      <Dialog
        open={!!refundTarget}
        onClose={() => !refundInFlight && setRefundTarget(null)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {t('posTransactions.refund.title', { id: refundTarget?.id })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <NumericInput
              label={t('posTransactions.refund.amount')}
              value={refundAmount}
              onChange={setRefundAmount}
              allowDecimals
              min={0}
              max={refundTarget ? Number(refundTarget.amount) : undefined}
              fullWidth
              helperText={
                refundTarget
                  ? t('posTransactions.refund.max', {
                      amount: Number(refundTarget.amount).toFixed(2),
                      currency: refundTarget.currency
                    })
                  : ''
              }
            />
            <TextField
              label={t('posTransactions.refund.reason')}
              value={refundReason}
              onChange={(event) => setRefundReason(event.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRefundTarget(null)}
            disabled={refundInFlight}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={submitRefund}
            disabled={refundInFlight}
          >
            {t('posTransactions.refund.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <OffDevicePosRefundDialog
        transaction={offDeviceTarget}
        onClose={() => setOffDeviceTarget(null)}
        onRecorded={onOffDeviceRecorded}
      />
    </Card>
  );
};

export default BranchPosTransactionsManager;
